import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { draftConversationId } from '@/slices/chat/chatSlice';
import { scrollToBlockWhenRendered } from './blocks/addedBlockScroll';
import type { ChatConversationsController } from './useChatConversations';

interface ChatPageRouteOptions {
  /**
   * Whether this surface owns the URL right now. The OS desktop stays mounted
   * beneath every window, where the query string belongs to the window's
   * route: while disabled nothing is read from or written to it. On return
   * the URL is read afresh — a bare route resumes the thread the desktop was
   * on when it was covered (the drawer may have moved the shared active
   * thread meanwhile), and an id in the URL is selected as always.
   */
  enabled?: boolean;
}

/**
 * The full chat page's URL contract (ORA-458): `?conversationId=` opens and
 * follows the thread being read, a bare route is New Chat, and `?blockId=`
 * is a return from a study set (ORA-681). Shared by the `/chat` page and the
 * AI desktop at `/`, which is the same surface.
 */
export const useChatPageRoute = (
  conversations: ChatConversationsController,
  { enabled = true }: ChatPageRouteOptions = {}
): void => {
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    activeConversationId,
    isResolvingThread,
    isThreadReady,
    reload,
    select,
    shouldStartNewThread,
    startNew,
  } = conversations;

  const requestedId = searchParams.get('conversationId') || '';
  // Coming back from a study set (ORA-681). Only the return writes this param,
  // so its presence is the signal to re-read the thread: the row the return
  // wrote is not in the copy this client is holding.
  const returnedBlockId = searchParams.get('blockId') || '';
  const handledReturnBlockId = useRef<string | null>(null);
  const cancelReturnScroll = useRef<(() => void) | null>(null);
  const handledRouteId = useRef<string | null>(null);
  const pendingRouteId = useRef<string | null>(null);
  const explicitRouteWasActivated = useRef(false);
  const [bareRouteResetIssued, setBareRouteResetIssued] = useState(false);
  const [routeSyncVersion, setRouteSyncVersion] = useState(0);
  const wasEnabled = useRef(enabled);
  const suspendedConversationId = useRef<string | null>(null);

  // Covered by a window: remember the desktop's own thread and forget the
  // route handling, so the URL is handled again when the desktop returns
  // instead of being matched against what it handled before the window.
  // Declared first so it runs ahead of the route effect on the same render.
  useLayoutEffect(() => {
    if (wasEnabled.current === enabled) return;
    wasEnabled.current = enabled;
    if (enabled) return;

    const isDraft =
      activeConversationId?.startsWith(draftConversationId('')) ?? false;

    suspendedConversationId.current =
      activeConversationId && !isDraft ? activeConversationId : null;

    handledRouteId.current = null;
    pendingRouteId.current = null;
    explicitRouteWasActivated.current = false;
    setBareRouteResetIssued(false);
  }, [activeConversationId, enabled]);

  // A bare route is an explicit New Chat destination. Handle each URL target
  // before the conversation hook's passive generic-thread lookup runs, so an
  // active drawer thread or a prior generic thread cannot fill this route.
  // The ref makes this a route transition, rather than reacting to every
  // store update while a first turn is being assigned its server id.
  useLayoutEffect(() => {
    if (!enabled) return;
    if (handledRouteId.current === requestedId) return;
    handledRouteId.current = requestedId;

    // Back from a window on a bare route: the desktop's thread, not New Chat
    // and not whatever the drawer left active. An id in the URL still wins.
    const target = requestedId || suspendedConversationId.current || '';
    suspendedConversationId.current = null;
    pendingRouteId.current = target;

    if (target) {
      explicitRouteWasActivated.current = false;
      setBareRouteResetIssued(false);

      void select(target).finally(() => {
        if (pendingRouteId.current !== target) return;
        pendingRouteId.current = null;
        setRouteSyncVersion((version) => version + 1);
      });

      return;
    }

    setBareRouteResetIssued(true);
    if (!shouldStartNewThread) startNew();
  }, [enabled, requestedId, select, shouldStartNewThread, startNew]);

  useLayoutEffect(() => {
    if (!enabled) return;
    const pending = pendingRouteId.current;
    if (!pending) return;

    if (activeConversationId === pending) {
      explicitRouteWasActivated.current = true;
      return;
    }

    // A history selection or New Chat action superseded the route selection.
    // Do not wait for its read to settle before clearing the old URL; its
    // resolution token keeps a late response from hydrating the new thread.
    if (explicitRouteWasActivated.current && activeConversationId !== pending) {
      pendingRouteId.current = null;
      setRouteSyncVersion((version) => version + 1);
    }
  }, [activeConversationId, enabled]);

  // Keep the URL on the conversation being read, so a reload or a shared link
  // lands back on it — the same contract the legacy page had. A draft id is
  // the slice's own and means nothing to anyone else, so it never goes in.
  useEffect(() => {
    if (!enabled) return;

    const isDraft =
      activeConversationId?.startsWith(draftConversationId('')) ?? false;

    const pending = pendingRouteId.current;
    if (pending !== null) {
      if (pending === '') {
        // The effect that requested New Chat has not committed yet. Its prior
        // render may still have a drawer thread in this closure, so it cannot
        // be reflected into the URL.
        if (!bareRouteResetIssued || !activeConversationId || isDraft) return;
      } else if (isResolvingThread || activeConversationId !== pending) {
        return;
      }

      pendingRouteId.current = null;
    }

    if (!isThreadReady || isResolvingThread) return;

    const next = activeConversationId && !isDraft ? activeConversationId : '';
    if ((searchParams.get('conversationId') || '') === next) return;

    setSearchParams(next ? { conversationId: next } : {}, { replace: true });
  }, [
    activeConversationId,
    bareRouteResetIssued,
    enabled,
    isResolvingThread,
    isThreadReady,
    routeSyncVersion,
    searchParams,
    setSearchParams,
  ]);

  /**
   * The return row and the set's new footer state, then the block the student
   * left from. The re-read is what brings the row in; the scroll retries for a
   * few frames because the thread is still rendering when it is asked for.
   */
  useEffect(() => {
    if (!enabled) return;
    if (!returnedBlockId || !requestedId) return;
    if (!isThreadReady || isResolvingThread) return;
    if (activeConversationId !== requestedId) return;
    if (handledReturnBlockId.current === returnedBlockId) return;
    handledReturnBlockId.current = returnedBlockId;

    void reload(requestedId)
      .catch(() => undefined)
      .finally(() => {
        cancelReturnScroll.current = scrollToBlockWhenRendered(returnedBlockId);

        // The block id has done its work. Left in place it would be a reload
        // away from replaying the return, and it would ride along in anything
        // the student shares or bookmarks from here.
        setSearchParams({ conversationId: requestedId }, { replace: true });
      });
  }, [
    activeConversationId,
    enabled,
    isResolvingThread,
    isThreadReady,
    reload,
    requestedId,
    returnedBlockId,
    setSearchParams,
  ]);

  // Held across the URL rewrite above — which re-runs the effect that started
  // it — so the retrying scroll is cancelled only when the surface itself goes.
  useEffect(() => () => cancelReturnScroll.current?.(), []);
};
